// `HeroSprite` (11 §10.10): a figure for the roster, lobby line-up and aftermath. A hero (by `heroId` or a
// `look`) is drawn from the hero kit (docs/145, `HeroFigure`); anything else is an Oryx creature frame as a CSS
// sprite from the creatures atlas, scaled by integers, falling back to a class glyph when the atlas is not served.
import { useEffect, useState } from 'react';
import { CLASS_FRAMES } from '../../core/sim/adapt';
import { hugeArt } from '../../game/hugeArt';
import { creatureArt } from '../../game/creatureArt';
import type { HeroLook } from '../../game/heroKit';
import { HeroFigure } from './HeroFigure';

interface Frame { x: number; y: number; w: number; h: number }
let sheet = { w: 512, h: 512 };
let framesPromise: Promise<Record<string, Frame>> | undefined;
function loadFrames(): Promise<Record<string, Frame>> {
  framesPromise ??= fetch('/assets/oryx/creatures.json').then(async (r) => {
    if (!r.ok) return {};
    const j = (await r.json()) as { frames: Record<string, { frame: Frame }>; meta?: { size?: { w: number; h: number } } };
    if (j.meta?.size) sheet = j.meta.size;
    return Object.fromEntries(Object.entries(j.frames).map(([k, v]) => [k, v.frame]));
  }).catch(() => ({}));
  return framesPromise;
}

export function useCreatureFrames(): Record<string, Frame> | undefined {
  const [frames, setFrames] = useState<Record<string, Frame>>();
  useEffect(() => { void loadFrames().then(setFrames); }, []);
  return frames;
}

export const CLASS_GLYPH: Record<string, string> = { fighter: '⚔', rogue: '🗡', wizard: '✦', cleric: '✚', ranger: '➶', paladin: '☀', druid: '♣', bard: '♪' };

export function HeroSprite({ frame, classId, scale = 2, title, heroId, look, species, breathe }: { frame?: number; classId?: string; scale?: 2 | 3 | 4; title?: string; heroId?: bigint | number; look?: HeroLook; species?: number; breathe?: boolean }) {
  // docs/145: a hero is always drawn from the hero kit, never an Oryx frame
  if (heroId !== undefined || look) return <HeroFigure heroId={heroId} look={look} species={species} classId={classId} scale={scale} title={title ?? classId} breathe={breathe} />;
  return <CreatureSprite frame={frame} classId={classId} scale={scale} title={title} />;
}

/** An Oryx creature frame (or a commissioned creature's own art): NPCs, foes and beasts. */
function CreatureSprite({ frame, classId, scale = 2, title }: { frame?: number; classId?: string; scale?: 2 | 3 | 4; title?: string }) {
  const frames = useCreatureFrames();
  // docs/92 HM5: a commissioned huge body is its own image, shown a little larger than a frame
  // would be (it is the fight's wonder) and scaled to whole art pixels where it can be; docs/95:
  // every one faces west as the most of them are drawn, so a body drawn facing east is mirrored
  const art = hugeArt(frame);
  if (art) {
    const box = 24 * scale * 1.5;
    return (
      <span className="fk-sprite fk-sprite--huge" role="img" aria-label={title ?? art.name} title={title ?? art.name}
        style={{ width: box, height: box, backgroundImage: `url(${art.url})`, backgroundSize: 'contain', backgroundRepeat: 'no-repeat', backgroundPosition: 'center bottom', imageRendering: 'pixelated', ...(art.faces === 'right' ? { transform: 'scaleX(-1)' } : {}) }} />
    );
  }
  // docs/117: a commissioned one-square creature is its own 24 px image, scaled as a frame is
  const small = creatureArt(frame);
  if (small) {
    return (
      <span className="fk-sprite" role="img" aria-label={title ?? small.name} title={title ?? small.name}
        style={{ width: 24 * scale, height: 24 * scale, backgroundImage: `url(${small.url})`, backgroundSize: '100% 100%', backgroundRepeat: 'no-repeat', imageRendering: 'pixelated' }} />
    );
  }
  const idx = frame && frame > 0 ? frame : CLASS_FRAMES[classId ?? 'fighter'] ?? 1;
  const f = frames?.[`creature_${String(idx).padStart(3, '0')}`];
  if (!f) return <span className="fk-sprite fk-sprite--glyph" style={{ width: 24 * scale, height: 24 * scale, fontSize: 12 * scale }} aria-hidden="true">{CLASS_GLYPH[classId ?? ''] ?? '●'}</span>;
  return (
    <span className="fk-sprite" role="img" aria-label={title ?? classId ?? 'hero'} title={title}
      style={{ width: f.w * scale, height: f.h * scale, backgroundImage: 'url(/assets/oryx/creatures.png)', backgroundPosition: `-${f.x * scale}px -${f.y * scale}px`, backgroundSize: `${sheet.w * scale}px ${sheet.h * scale}px` }} />
  );
}
